import store from '@/store/store'
import i18n from '@/i18n'

/**
 * Login session detector.
 *
 * The app only used to check whether an `access_token` STRING existed in
 * localStorage. It never checked whether that token was still valid, so a page
 * that makes no API call (e.g. paparanutama) would open fine with a dead token,
 * while the next page that hits the backend got a 401/403 and the user was
 * silently kicked to login. This service centralises real session detection.
 */

// All localStorage keys that make up an authenticated session.
const AUTH_KEYS = ['access_token', 'user', 'tokenexport', 'datapp', 'datapo', 'hid']

// sessionStorage flag: survives the redirect/reload so the login page can show
// a "session expired" toast. Kept in sessionStorage so localStorage.clear()
// (used elsewhere) does not wipe it.
const EXPIRED_NOTICE = 'session_expired_notice'

// Treat the token as expired this many ms BEFORE its real exp, so we act just
// before the backend starts rejecting requests.
const EXPIRY_SKEW_MS = 5 * 1000

let watchTimer = null
let redirecting = false

export function getToken() {
  return window.localStorage.getItem('access_token')
}

/**
 * Best-effort decode of a JWT payload. Returns null for opaque (non-JWT)
 * tokens or anything malformed — callers must handle null.
 */
export function decodeJwt(token) {
  try {
    const parts = String(token).split('.')
    if (parts.length !== 3) return null
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

/**
 * Expiry of the current token in ms since epoch, or null when it cannot be
 * determined client-side (opaque token / no `exp` claim). When null we cannot
 * proactively detect expiry and must rely on the backend's 401/403 response.
 */
export function getTokenExpiry() {
  const token = getToken()
  if (!token) return null
  const payload = decodeJwt(token)
  if (payload && payload.exp) return payload.exp * 1000
  return null
}

export function isTokenExpired() {
  const expiry = getTokenExpiry()
  if (expiry === null) return false // unknown -> not proactively expired
  return Date.now() >= expiry - EXPIRY_SKEW_MS
}

export function isAuthenticated() {
  return !!getToken() && !isTokenExpired()
}

export function clearSession() {
  AUTH_KEYS.forEach((key) => window.localStorage.removeItem(key))
}

export function showExpiredToast() {
  store.commit('SET_SNACKBAR', {
    showing: true,
    text: i18n.t('sesitamat'),
    color: 'red darken-2',
    btncolor: 'white',
    timeout: 5000,
  })
}

/** Flag that the next landing on the login page should show the expiry toast. */
export function markExpiredNotice() {
  window.sessionStorage.setItem(EXPIRED_NOTICE, '1')
}

/**
 * End the session and send the user to the login page with a toast.
 * Safe to call from anywhere (axios interceptor, router guard, timer).
 */
export function expireSession() {
  if (redirecting) return
  redirecting = true
  clearSession()
  markExpiredNotice()
  window.location.href = process.env.VUE_APP_INITIAL_URL || `/${i18n.locale}/`
}

/**
 * Called once on app start. If we arrived here because the session expired,
 * show the toast and clear the flag.
 */
export function flushExpiredNotice() {
  if (window.sessionStorage.getItem(EXPIRED_NOTICE)) {
    window.sessionStorage.removeItem(EXPIRED_NOTICE)
    showExpiredToast()
  }
}

/**
 * Proactively watch for expiry (so an idle page with no API traffic still
 * detects the timeout) and sync logout across browser tabs.
 */
export function startSessionWatch() {
  if (watchTimer) return

  watchTimer = window.setInterval(() => {
    if (getToken() && isTokenExpired()) {
      expireSession()
    }
  }, 15 * 1000)

  // Cross-tab: if another tab logs out / in, react in this tab too.
  window.addEventListener('storage', (event) => {
    if (event.key === 'access_token' && !event.newValue && getToken() === null) {
      if (!redirecting) {
        redirecting = true
        window.location.href = process.env.VUE_APP_INITIAL_URL || `/${i18n.locale}/`
      }
    }
  })
}
